import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { ChapterEnum, PriceListUerRowInput, WorkOrderChangeObjectItem } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { AddWorkOrderPriceForm } from 'pages/Project/Common/AddWorkOrderPriceForm/';
import type { IObjectTabFilters, StepsContent } from 'pages/Project/Common/AddWorkOrderPriceForm/helpersAndTypes';
import { DEFAULT_ADD_UER_STEPS } from 'pages/Project/Common/AddWorkOrderPriceForm/helpersAndTypes';
import { CommonValueNoteCellRenderer } from 'pages/Project/Common/AddWorkOrderPriceForm/ValueNoteCellRenderer';
import { Button, cnMixSpace } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import { type ElementRef, useCallback, useMemo, useRef, useState } from 'react';

import { CreateOrEditUer } from '../../Uer/CreateOrEditUer';

import { useAddPricesToWorkOrderChange, useWorkOrderChangeTableRows } from './hooks';

export const AddWorkOrderChangeUer = ({
    onClose,
    objects,
    constructionTypeId,
    workOrderChangeId,
    reloadWorkOrderTable,
    objectId,
    subObjectId,
    contractId,
    catalogName,
    catalogVersion,
}: {
    onClose: () => void;
    objects: WorkOrderChangeObjectItem[];
    constructionTypeId: number;
    workOrderChangeId: number;
    objectId: number | undefined;
    contractId: number | undefined;
    subObjectId: number | undefined;
    reloadWorkOrderTable: (objectIds: number[]) => void;
    catalogName: string;
    catalogVersion: string;
}) => {
    const controlRef = useRef<ElementRef<typeof AddWorkOrderPriceForm>>(null);

    const [selectedChapter, setSelectedChapter] = useState<ChapterEnum | undefined>(undefined);
    const [valuesNotes, setValuesNotes] = useState<Record<number, string>>({});
    const [selectedLeafObjects, setSelectedLeafObjects] = useState<number[] | undefined>(undefined);
    const [selectedRowBaseRowIds, setSelectedRowBaseRowIds] = useState<number[] | undefined>(undefined);

    const objectIdsVariables = useMemo(() => {
        if (selectedLeafObjects) {
            return objects
                .filter((object) => selectedLeafObjects.includes(object.id))
                .map((object) => ({
                    workOrderObjectId: object?.workOrderObjectId,
                    workOrderChangeObjectId: object?.workOrderChangeObjectId,
                }));
        }

        return [];
    }, [objects, selectedLeafObjects]);

    const {
        dataSource,
        onSearchStringClear,
        searchString,
        onSearch,
        onSearchStringChange,
        filters,
        changeFilterHandler,
    } = useWorkOrderChangeTableRows({
        constructionTypeId,
        workOrderChangeId,
        chapter: selectedChapter,
        objectIds: objectIdsVariables,
    });

    const checkedDataSender = useCallback(
        ({
            objectTabFilters,
            checkedLeafObjects,
            uerRowBaseRowIds,
        }: {
            checkedLeafObjects: number[];
            uerRowBaseRowIds: number[];
            objectTabFilters: IObjectTabFilters;
        }) => {
            setSelectedChapter(objectTabFilters?.chapter?.id);
            setSelectedLeafObjects(checkedLeafObjects);
            setSelectedRowBaseRowIds(uerRowBaseRowIds);
        },
        [],
    );

    const uerInputs = useMemo<PriceListUerRowInput[] | undefined>(
        () =>
            selectedRowBaseRowIds?.map((id) => ({
                uerBaseRowId: id,
                rationale: valuesNotes?.[id],
            })),
        [selectedRowBaseRowIds, valuesNotes],
    );

    const { addPricesToWorkOrderChangeHandler, isLoading } = useAddPricesToWorkOrderChange({
        workOrderChangeId,
        chapter: selectedChapter,
        workOrderChangeObjectIds: objectIdsVariables,
        reloadWorkOrderTable,
        constructionTypeId,
        onClose,
        uerInputs,
    });

    const isNextStepDisabled = useMemo(
        () => !(selectedChapter && Object.values(selectedLeafObjects ?? {})?.some((checked) => checked)),
        [selectedChapter, selectedLeafObjects],
    );

    const firstStepContent = useCallback(
        (onStepClickCreator: (step: (typeof DEFAULT_ADD_UER_STEPS)[number]) => () => void) => (
            <Button
                label="Далее"
                onClick={onStepClickCreator(DEFAULT_ADD_UER_STEPS[1])}
                disabled={isNextStepDisabled}
                size="m"
            />
        ),
        [isNextStepDisabled],
    );
    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const lastStepContent = useCallback(
        (onStepClickCreator: (step: (typeof DEFAULT_ADD_UER_STEPS)[number]) => () => void) => (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                <CreateOrEditUer
                    constructionTypeId={constructionTypeId}
                    onSave={() => undefined}
                    contractId={contractId}
                    chapter={selectedChapter}
                    setCheckedIds={controlRef?.current?.setCheckedPrices}
                    resetPageData={controlRef?.current?.refreshAddDataTable}
                    accessRules={workOrderChangeUerRules}
                    catalogName={catalogName}
                    catalogVersion={catalogVersion}
                    fromWorkOrder
                />
                <div>
                    <Button
                        size="s"
                        label="Добавить в Изм. к НЗ"
                        onClick={addPricesToWorkOrderChangeHandler}
                        disabled={!selectedRowBaseRowIds?.length}
                        className={cnMixSpace({
                            mR: 'm',
                        })}
                    />
                    <Button
                        size="s"
                        view="ghost"
                        label="Назад"
                        onClick={onStepClickCreator(DEFAULT_ADD_UER_STEPS[0])}
                    />
                </div>
            </div>
        ),
        [
            addPricesToWorkOrderChangeHandler,
            catalogName,
            catalogVersion,
            constructionTypeId,
            contractId,
            selectedChapter,
            selectedRowBaseRowIds?.length,
            workOrderChangeUerRules,
        ],
    );

    const stepsContent = useMemo<StepsContent>(
        () => ({
            0: firstStepContent,
            1: lastStepContent,
        }),
        [lastStepContent, firstStepContent],
    );

    const initiallyCheckedLeafObjects = useMemo(() => {
        if (subObjectId) {
            return {
                [subObjectId]: true,
            };
        }

        return {
            ...(objectId && { [objectId]: true }),
        };
    }, [objectId, subObjectId]);

    const initiallyCheckedObj = useMemo(() => objects?.find((o) => o.id === objectId), [objectId, objects]);

    const extraColumnDefs = useMemo<ColDef[]>(
        () => [
            {
                field: 'valueNote',
                headerName: 'Примечание',
                flex: 1,
                cellRenderer: CommonValueNoteCellRenderer,
                cellRendererParams: {
                    valuesNotes,
                    setValuesNotes,
                    checkedIds: selectedRowBaseRowIds,
                },
            },
        ],
        [selectedRowBaseRowIds, valuesNotes],
    );

    const filterVariables = useMemo(
        () => ({
            workOrderChangeId,
            constructionTypeId,
            objectIds: objectIdsVariables,
            chapter: selectedChapter,
        }),
        [constructionTypeId, objectIdsVariables, selectedChapter, workOrderChangeId],
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const handleClose = useCallback(() => {
        const isDirty = Boolean(selectedChapter && selectedLeafObjects?.length);

        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [handleOpenConfirmationDialog, onClose, selectedChapter, selectedLeafObjects?.length]);

    return (
        <>
            <AddWorkOrderPriceForm
                ref={controlRef}
                onClose={handleClose}
                objects={objects as any}
                isLoading={isLoading}
                constructionTypeId={constructionTypeId}
                stepsContent={stepsContent}
                onSearchStringClear={onSearchStringClear}
                searchString={searchString}
                dataSource={dataSource}
                onSearchStringChange={onSearchStringChange}
                filters={filters}
                onSearch={onSearch}
                checkedDataSender={checkedDataSender}
                initiallyCheckedLeafs={initiallyCheckedLeafObjects}
                initiallyCheckedObj={initiallyCheckedObj as any}
                extraColumnDefs={extraColumnDefs}
                filterVariables={filterVariables}
                changeFilterHandler={changeFilterHandler}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

AddWorkOrderChangeUer.displayName = 'AddWorkOrderChangeUer';
